<template>
  <li :class="DS_MENU_CLASSES.Entry">
    <div
      :class="itemClasses"
      :data-cy="`ds-menu__item--${itemDataCySuffix}`"
    >
      <component
        :is="actionTag"
        :class="DS_MENU_CLASSES.ItemContent"
        :href="actionHref"
        :type="actionTag === 'button' ? 'button' : undefined"
        :disabled="actionTag === 'button' ? props.item.disabled : undefined"
        :aria-disabled="props.item.disabled || undefined"
        :aria-current="props.item.href && props.item.selected ? 'page' : undefined"
        :tabindex="actionTag === 'a' && props.item.disabled ? -1 : undefined"
        :data-turbo="hasTurboAttributes || undefined"
        :data-turbo-frame="actionTag === 'a' ? props.item.turboFrame : undefined"
        :data-turbo-method="actionTag === 'a' ? props.item.turboMethod : undefined"
        :data-turbo-confirm="actionTag === 'a' ? props.item.turboConfirm : undefined"
        :data-cy="actionDataCy"
        @click="selectItem"
      >
        <DSIcon
          v-if="props.item.icon"
          :class="DS_MENU_CLASSES.Icon"
          :icon="props.item.icon"
          :size="props.size"
        />

        <span
          v-if="hasPrefixContent"
          :class="DS_MENU_CLASSES.Prefix"
          :data-cy="`${DS_MENU_CLASSES.Prefix}--${props.item.value}`"
        >
          <slot
            name="prefix"
            v-bind="props.item"
          />
        </span>

        <span :class="DS_MENU_CLASSES.Text">
          <span
            ref="titleRef"
            v-ds-tooltip="titleTooltip"
            :class="DS_MENU_CLASSES.Title"
            :data-cy="`ds-menu__title--${itemDataCySuffix}`"
          >
            {{ props.item.label }}
          </span>
          <span
            v-if="props.item.subtitle"
            ref="subtitleRef"
            v-ds-tooltip="subtitleTooltip"
            :class="DS_MENU_CLASSES.Subtitle"
            :data-cy="`ds-menu__subtitle--${itemDataCySuffix}`"
          >
            {{ props.item.subtitle }}
          </span>
        </span>

        <span
          v-if="hasSuffixContent"
          :class="DS_MENU_CLASSES.Suffix"
          :data-cy="`ds-menu__suffix--${itemDataCySuffix}`"
        >
          <slot
            name="suffix"
            v-bind="props.item"
          />
        </span>
      </component>

      <span
        v-if="hasChildren"
        :class="DS_MENU_CLASSES.SubmenuTrigger"
        :data-cy="`ds-menu__submenu-trigger--${itemDataCySuffix}`"
      >
        <DSButton
          variant="ghost"
          :size="props.size"
          :prefix-icon="expanded ? 'chevron-top' : 'chevron-down'"
          :aria-label="props.item.label"
          :aria-expanded="expanded"
          :disabled="props.item.disabled"
          @click="toggleSubmenu"
        />
      </span>
    </div>

    <ul
      v-if="hasChildren && expanded"
      :class="DS_MENU_CLASSES.Hierarchy"
      :data-cy="`ds-menu__hierarchy--${itemDataCySuffix}`"
    >
      <template
        v-for="child in props.item.children"
        :key="child.value"
      >
        <DSMenuSeparator
          v-if="child.type === DS_MENU_ITEM_TYPE.Separator"
          :item="child"
        />
        <DSMenuItem
          v-else
          :item="child"
          :size="props.size"
          :expanded-values="props.expandedValues"
          @select="emit('select', $event)"
          @submenu:toggle="forwardSubmenuToggle"
        >
          <template
            v-if="slots.prefix"
            #prefix="slotProps"
          >
            <slot
              name="prefix"
              v-bind="slotProps"
            />
          </template>
          <template
            v-if="slots.suffix"
            #suffix="slotProps"
          >
            <slot
              name="suffix"
              v-bind="slotProps"
            />
          </template>
        </DSMenuItem>
      </template>
    </ul>
  </li>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import DSMenuSeparator from "./DSMenuSeparator.vue";
import { convertStringToDataCy } from "@/src/shared/utils/playwright";
import { hasSlotContent } from "@/src/shared/utils/slots";
import { useDSTruncationTooltip } from "@/src/design_system/shared/composables/useDSTruncationTooltip";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";
import { DS_TOOLTIP_PLACEMENTS } from "@/src/design_system/primitives/tooltip/types/options";
import { DS_MENU_CLASSES } from "@/src/design_system/navigations/menu/constants/classes";
import {
  DS_MENU_ITEM_SEVERITY,
  DS_MENU_ITEM_TYPE,
  type DSMenuActionItem,
  type DSMenuItemSeverity,
  type DSMenuSize,
  type DSMenuSlots,
} from "@/src/design_system/navigations/menu/types";

interface Props {
  item: DSMenuActionItem;
  size: DSMenuSize;
  expandedValues: string[];
}

interface Emits {
  (e: "select", item: DSMenuActionItem): void;
  (e: "submenu:toggle", item: DSMenuActionItem, expanded: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const slots = defineSlots<DSMenuSlots>();

const titleRef = ref<HTMLElement>();
const subtitleRef = ref<HTMLElement>();

const { tooltip: titleTooltip } = useDSTruncationTooltip({
  elementRef: titleRef,
  text: computed(() => props.item.label),
  placement: DS_TOOLTIP_PLACEMENTS.BottomStart,
});

const { tooltip: subtitleTooltip } = useDSTruncationTooltip({
  elementRef: subtitleRef,
  text: computed(() => props.item.subtitle),
  placement: DS_TOOLTIP_PLACEMENTS.BottomStart,
});

const hasChildren = computed(() => Boolean(props.item.children?.length));
const expanded = computed(() => props.expandedValues.includes(props.item.value));
const actionTag = computed(() => (props.item.href ? "a" : "button"));
const actionHref = computed(() => (props.item.disabled ? undefined : props.item.href));
const hasTurboAttributes = computed(
  () => actionTag.value === "a" && Boolean(props.item.turboFrame || props.item.turboMethod || props.item.turboConfirm)
);
const itemDataCySuffix = computed(() => convertStringToDataCy(props.item.value));
const actionDataCy = computed(() => props.item.dataCy || `ds-menu__item-content--${itemDataCySuffix.value}`);
const hasPrefixContent = computed(() => hasSlotContent(slots, "prefix", props.item));
const hasSuffixContent = computed(() => hasSlotContent(slots, "suffix", props.item));
const severityClass: Record<DSMenuItemSeverity, string> = {
  [DS_MENU_ITEM_SEVERITY.Default]: DS_MENU_CLASSES.ItemDefault,
  [DS_MENU_ITEM_SEVERITY.Danger]: DS_MENU_CLASSES.ItemDanger,
};

const itemClasses = computed(() => [
  DS_MENU_CLASSES.Item,
  severityClass[props.item.severity ?? DS_MENU_ITEM_SEVERITY.Default],
  {
    "is-selected": Boolean(props.item.href && props.item.selected),
    "is-disabled": props.item.disabled,
  },
]);

const selectItem = (event: MouseEvent) => {
  if (props.item.disabled) {
    event.preventDefault();
    return;
  }

  emit("select", props.item);
};

const toggleSubmenu = () => {
  if (props.item.disabled) return;

  emit("submenu:toggle", props.item, !expanded.value);
};

const forwardSubmenuToggle = (item: DSMenuActionItem, nextExpanded: boolean) => {
  emit("submenu:toggle", item, nextExpanded);
};
</script>
